import React, {useEffect} from 'react';
import {motion} from 'motion/react';

import {DotsPagination} from '../pagination/dots-pagination';
import styles from './card-stack.module.scss';

const CARD_OFFSET = 30;
const SCALE_FACTOR = 0.06;

/**
 * Props for the CardStack component.
 * @property data - Array of card data with unique id and children nodes.
 * @property onMoveToEnd - Callback to move a card to the end of the stack.
 */
interface CardStackProps {
    data: {id: string; children: React.ReactNode}[];
    onMoveToEnd: (index: number) => void;
    autoPlay: boolean;
    setAutoPlay: (autoPlay: boolean) => void;
    isFlipped: boolean;
}

/**
 * CardStack component displays a stack of cards with autoplay and drag-to-move functionality.
 *
 * Note: The autoplay useEffect includes onMoveToEnd and data in its dependency array to avoid stale closures,
 * ensuring the interval always uses the latest callback and data.
 */
export const CardStack = ({
    data,
    autoPlay,
    isFlipped,
    setAutoPlay,
    onMoveToEnd,
}: CardStackProps) => {
    useEffect(() => {
        if (autoPlay && !isFlipped) {
            const interval = setInterval(() => {
                onMoveToEnd(0);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [autoPlay, onMoveToEnd, data, isFlipped]);

    return (
        <div className={styles.container}>
            <div className={styles.wrapperStyle}>
                <div
                    className={styles.cardWrapStyle}
                    onMouseEnter={() => setAutoPlay(false)}
                    onMouseLeave={() => setAutoPlay(true)}
                >
                    {data.map((item, index) => {
                        const canDrag = index === 0;

                        return (
                            <motion.div
                                key={item.id}
                                className={styles.cardStyle}
                                style={{
                                    cursor: canDrag ? 'grab' : 'auto',
                                }}
                                initial={{
                                    y: 0,
                                    top: index * -CARD_OFFSET,
                                    scale: 1 - index * SCALE_FACTOR,
                                    zIndex: data.length - index,
                                }}
                                animate={{
                                    y: 0,
                                    top: index * -CARD_OFFSET,
                                    scale: 1 - index * SCALE_FACTOR,
                                    zIndex: data.length - index,
                                }}
                                drag={canDrag ? 'y' : false}
                                dragConstraints={{
                                    top: 0,
                                    bottom: 0,
                                }}
                                onDragEnd={() => onMoveToEnd(index)}
                            >
                                {item.children}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
            <DotsPagination data={data} activeIndex={parseInt(data[0].id)} />
        </div>
    );
};
